import React, { useState, useEffect, useRef, useMemo } from "react";

const SpiteAnimator = ({ 
  spriteUrl, 
  spriteImport, 
  width = 400, 
  height = 400, 
  autoPlay = false,
  showControls = true,
  showFrameCounter = true,
  className = "",
  style = {},
  onAnimationStart,
  onAnimationComplete
}) => {
  const [spriteLoaded, setSpriteLoaded] = useState(false);
  const [currentFrame, setCurrentFrame] = useState('-');
  const [isAnimating, setIsAnimating] = useState(false);
  const spriteRef = useRef(null);
  const containerRef = useRef(null);

  // Use either spriteUrl or spriteImport
  const imageSource = spriteUrl || spriteImport;

  useEffect(() => {
    if (imageSource) {
      const img = new Image();
      img.onload = () => {
        setSpriteLoaded(true);
      };
      img.src = imageSource;
    }
  }, [imageSource]);

  // Auto-play effect
  useEffect(() => {
    if (autoPlay && spriteLoaded) {
      triggerExplosion();
    }
  }, [autoPlay, spriteLoaded]);

  const createParticles = (container, count = 15) => {
    if (!container) return;
    
    const rect = container.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    for (let i = 0; i < count; i++) {
      const particle = document.createElement('div');
      particle.className = 'spite-particle spite-particle-burst';

      const angle = (360 / count) * i + (Math.random() * 30 - 15);
      const distance = 40 + Math.random() * 60;
      const dx = Math.cos(angle * Math.PI / 180) * distance;
      const dy = Math.sin(angle * Math.PI / 180) * distance;

      particle.style.cssText = `
        position: absolute;
        width: 4px;
        height: 4px;
        background: #ff6b35;
        border-radius: 50%;
        pointer-events: none;
        left: ${centerX}px;
        top: ${centerY}px;
        --dx: ${dx}px;
        --dy: ${dy}px;
        background-color: hsl(${Math.random() * 60 + 10}, 100%, ${50 + Math.random() * 30}%);
        animation: spiteParticleBurst 1s ease-out forwards;
      `;

      container.appendChild(particle);
      setTimeout(() => particle.remove(), 1000);
    }
  };

  const animateFullSequence = (sprite) => {
    if (!sprite || !spriteLoaded) return;

    // Reset sprite
    sprite.style.opacity = '1';
    sprite.style.transform = 'scale(1)';

    // Frame positions for 3x3 grid
    const frameSize = Math.min(width, height); // Full container size
    const frames = [
      `0px 0px`,
      `${-frameSize}px 0px`,
      `${-frameSize * 2}px 0px`,
      `0px ${-frameSize}px`,
      `${-frameSize}px ${-frameSize}px`,
      `${-frameSize * 2}px ${-frameSize}px`,
      `0px ${-frameSize * 2}px`,
      `${-frameSize}px ${-frameSize * 2}px`,
      `${-frameSize * 2}px ${-frameSize * 2}px`
    ];

    const frameDuration = 200;

    onAnimationStart?.();

    frames.forEach((position, index) => {
      setTimeout(() => {
        if (sprite) {
          sprite.style.backgroundPosition = position;
          setCurrentFrame(index + 1);
        }
      }, index * frameDuration);
    });

    // Animation complete
    setTimeout(() => {
      setIsAnimating(false);
      setCurrentFrame('-');
      onAnimationComplete?.();
    }, frames.length * frameDuration);
  };

  const triggerExplosion = () => {
    if (!spriteLoaded || isAnimating) return;

    setIsAnimating(true);
    const container = containerRef.current;
    const sprite = spriteRef.current;

    if (container && sprite) {
      createParticles(container, 15);
      animateFullSequence(sprite);
    }
  };

  const containerStyle = {
    position: 'relative',
    width: `${width}px`,
    height: `${height}px`,
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    cursor: 'pointer',
    ...style
  };

  const spriteStyle = {
    position: 'absolute',
    width: `${width}px`,
    height: `${height}px`,
    backgroundSize: `${width * 3}px ${height * 3}px`, // 3x3 grid scaling
    backgroundImage: spriteLoaded ? `url(${imageSource})` : 'none',
    backgroundRepeat: 'no-repeat',
    opacity: spriteLoaded ? 1 : 0,
    transform: 'scale(1)',
    zIndex: 2,
    transition: 'opacity 0.1s ease'
  };

  const centerDotStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '30px',
    height: '30px',
    background: 'transparent',
    borderRadius: '50%',
    transform: 'translate(-50%, -50%)',
    opacity: 0,
    zIndex: 1,
    transition: 'opacity 0.3s ease'
  };

  const frameCounterStyle = {
    position: 'absolute',
    top: '15px',
    right: '15px',
    background: 'rgba(0,0,0,0.8)',
    padding: '5px 10px',
    borderRadius: '15px',
    fontSize: '12px',
    zIndex: 3,
    color: '#fff'
  };

  const controlsStyle = {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '20px',
    gap: '15px'
  };

  const buttonStyle = {
    padding: '12px 24px',
    background: 'linear-gradient(45deg, #ff6b35, #f7931e)',
    border: 'none',
    borderRadius: '25px',
    color: 'white',
    fontWeight: 'bold',
    cursor: spriteLoaded ? 'pointer' : 'not-allowed',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(255, 107, 53, 0.4)',
    opacity: spriteLoaded ? 1 : 0.5
  };

  return (
    <div className={`spite-animator ${className}`}>
      <style>
        {`
          @keyframes spiteParticleBurst {
            0% {
              transform: scale(1) translate(0, 0);
              opacity: 1;
            }
            100% {
              transform: scale(0) translate(var(--dx, 0), var(--dy, 0));
              opacity: 0;
            }
          }
        `}
      </style>
      
      <div
        ref={containerRef}
        style={containerStyle}
        onClick={triggerExplosion}
      >
        <div style={centerDotStyle} />
        <div
          ref={spriteRef}
          style={spriteStyle}
        />
        
        {showFrameCounter && (
          <div style={frameCounterStyle}>
            Frame: <span>{currentFrame}</span>/9
          </div>
        )}
      </div>

      {showControls && (
        <div style={controlsStyle}>
          <button
            style={buttonStyle}
            onClick={triggerExplosion}
            disabled={!spriteLoaded || isAnimating}
            onMouseEnter={(e) => {
              if (spriteLoaded && !isAnimating) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(255, 107, 53, 0.6)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(255, 107, 53, 0.4)';
            }}
          >
            {isAnimating ? 'Animating...' : 'Trigger Explosion =�'}
          </button>
        </div>
      )}

      {!spriteLoaded && (
        <div style={{
          textAlign: 'center',
          marginTop: '10px',
          color: 'rgba(255,255,255,0.7)',
          fontSize: '14px'
        }}>
          {imageSource ? 'Loading sprite...' : 'No sprite provided'}
        </div>
      )}
    </div>
  );
};

export default SpiteAnimator;